import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Check, Link2, QrCode } from "lucide-react"
import { Button } from "@/components/ui/button"
import { type IntegrationQrCodeResponse } from "@/services/adminApi"

const downloadDataUrl = (filename: string, dataUrl: string) => {
  const anchor = document.createElement("a")
  anchor.href = dataUrl
  anchor.download = filename
  anchor.click()
}

export const IntegrationQrCodeResult = ({
  result,
  productName,
  mediaSrc,
}: {
  result: IntegrationQrCodeResponse
  productName?: string
  mediaSrc?: string
}) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const shareLink = result.targetUrl || result.shortLink
  const title = productName?.trim() || t("integrationInfo.qrCode.shareCatalogTitle")
  const isProductMedia = Boolean(mediaSrc)
  const displayedMedia = mediaSrc || result.downloads.png.dataUrl

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard write failed — leave state untouched
    }
  }

  const handleDownload = () => {
    downloadDataUrl(result.downloads.png.filename, result.downloads.png.dataUrl)
  }

  return (
    <div
      className="grid gap-5 sm:grid-cols-[320px_minmax(0,1fr)] sm:items-center sm:gap-8"
      data-testid="integration-qr-result"
    >
      <div className="h-[320px] w-[320px] overflow-hidden" data-testid="integration-qr-media">
        <img
          src={displayedMedia}
          alt={isProductMedia ? title : t("integrationInfo.qrCode.previewAlt")}
          className={isProductMedia ? "h-full w-full object-contain" : "h-full w-full"}
        />
      </div>

      <div className="flex flex-col gap-7">
        <div>
          <p className="text-sm text-muted-foreground">
            {t("integrationInfo.qrCode.shareSubtitle")}
          </p>
          <h3 className="mt-2 text-2xl font-bold leading-tight" data-testid="integration-qr-title">
            {title}
          </h3>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold">{t("integrationInfo.qrCode.linkStepTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("integrationInfo.qrCode.linkStepDescription")}
            </p>
          </div>
          <Button
            onClick={handleCopy}
            size="sm"
            data-testid="integration-qr-copy-link"
            className="shrink-0 bg-vto-accent text-vto-accent-foreground shadow hover:bg-vto-accent/90 focus-visible:ring-vto-accent"
          >
            {copied ? <Check data-icon="inline-start" /> : <Link2 data-icon="inline-start" />}
            {copied ? t("integrationInfo.qrCode.copied") : t("integrationInfo.qrCode.copyLink")}
          </Button>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold">{t("integrationInfo.qrCode.qrStepTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("integrationInfo.qrCode.qrStepDescription")}
            </p>
          </div>
          <Button
            onClick={handleDownload}
            size="sm"
            data-testid="integration-qr-download"
            className="shrink-0 bg-foreground text-background shadow hover:bg-foreground/90"
          >
            <QrCode data-icon="inline-start" />
            {t("integrationInfo.qrCode.downloadQrCode")}
          </Button>
        </div>
      </div>
    </div>
  )
}
